import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, ArrowRight, Calendar, Clock, Euro, Building, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { getSpecialiteColor } from '@/types/calendar';
import type { MembreDisponibilite } from '@/hooks/useDisponibilites';
import WeekendConfirmDialog, { isWeekend } from '@/components/ui/WeekendConfirmDialog';
import { calculerJoursOuvres } from '@/lib/heures-utils';
import { z } from "zod";

// Schema de validation pour la rotation d'un membre
const rotationFormSchema = z.object({
  nouveauChantierId: z.string().min(1, "Veuillez sélectionner un chantier"),
  dateRotation: z.string().min(1, "La date de rotation est requise"),
  dateFinAffectation: z.string().min(1, "La date de fin est requise"),
  heuresAncienChantier: z.number().min(0, "Les heures ne peuvent pas être négatives").max(12, "Maximum 12 heures"),
  heuresJour1: z.number().min(0.5, "Minimum 0.5 heure").max(12, "Maximum 12 heures"),
  heuresJoursSuivants: z.number().min(0.5, "Minimum 0.5 heure").max(12, "Maximum 12 heures"),
  notes: z.string().max(500, "500 caractères max"),
}).refine(
  data => data.dateFinAffectation >= data.dateRotation,
  { message: "La date de fin doit être après la date de rotation", path: ["dateFinAffectation"] }
);

interface ModalRotationProps {
  isOpen: boolean;
  onClose: () => void;
  membre: MembreDisponibilite | null;
  chantiers: any[];
  entrepriseId: string;
  onSuccess?: () => void;
}

/**
 * Modal de rotation d'un membre vers un autre chantier
 * - Termine l'affectation actuelle (date_fin = veille de rotation)
 * - Crée une nouvelle affectation sur le nouveau chantier
 */

const ModalRotation = ({
  isOpen,
  onClose,
  membre,
  chantiers,
  entrepriseId,
  onSuccess,
}: ModalRotationProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [nouveauChantierId, setNouveauChantierId] = useState('');
  const [dateRotation, setDateRotation] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateFinAffectation, setDateFinAffectation] = useState('');
  const [heuresAncienChantier, setHeuresAncienChantier] = useState(4); // Heures sur l'ancien chantier aujourd'hui
  const [heuresJour1, setHeuresJour1] = useState(4); // Heures pour aujourd'hui sur le nouveau chantier (jour de rotation)
  const [heuresJoursSuivants, setHeuresJoursSuivants] = useState(membre?.heures_par_jour || 8); // Heures pour les jours suivants
  const [notes, setNotes] = useState('');

  // État pour le popup de confirmation weekend
  const [showWeekendDialog, setShowWeekendDialog] = useState(false);
  const [pendingDateFin, setPendingDateFin] = useState<string>('');
  const [inclureWeekendFin, setInclureWeekendFin] = useState(false);

  // Calculer les jours ouvrés (avec prise en compte du weekend si confirmé)
  const joursOuvres = useMemo(() => {
    if (dateRotation && dateFinAffectation) {
      return calculerJoursOuvres(dateRotation, dateFinAffectation, { inclureWeekendFin });
    }
    return 0;
  }, [dateRotation, dateFinAffectation, inclureWeekendFin]);

  // Gérer le changement de date de fin avec vérification weekend
  const handleDateFinChange = (newDateFin: string) => {
    if (!newDateFin) {
      setDateFinAffectation('');
      return;
    }
    const date = parseISO(newDateFin);
    if (isWeekend(date)) {
      // La date tombe un weekend, demander confirmation
      setPendingDateFin(newDateFin);
      setShowWeekendDialog(true);
    } else {
      // Jour de semaine, appliquer directement
      setDateFinAffectation(newDateFin);
      setInclureWeekendFin(false);
    }
  };

  // Callback du dialogue weekend
  const handleWeekendConfirm = (inclure: boolean) => {
    setInclureWeekendFin(inclure);
    setDateFinAffectation(pendingDateFin);
    setShowWeekendDialog(false);
    setPendingDateFin('');
  };

  // Calculer heures_par_jour (moyenne pondérée si plusieurs jours)
  const heuresParJourMoyenne = useMemo(() => {
    if (joursOuvres <= 0) return heuresJour1;
    if (joursOuvres === 1) return heuresJour1;
    // Plusieurs jours : moyenne pondérée
    const totalHeures = heuresJour1 + (heuresJoursSuivants * (joursOuvres - 1));
    return totalHeures / joursOuvres;
  }, [joursOuvres, heuresJour1, heuresJoursSuivants]);

  // Pré-remplir la date de fin quand un chantier est sélectionné
  useEffect(() => {
    if (nouveauChantierId && chantiers) {
      const chantier = chantiers.find(c => c.id === nouveauChantierId);
      if (chantier?.date_fin_prevue || chantier?.date_fin_estimee) {
        setDateFinAffectation(chantier.date_fin_prevue || chantier.date_fin_estimee);
      } else {
        // Par défaut : demain
        const demain = new Date(dateRotation);
        demain.setDate(demain.getDate() + 1);
        setDateFinAffectation(format(demain, 'yyyy-MM-dd'));
      }
    }
  }, [nouveauChantierId, chantiers, dateRotation]);

  if (!isOpen || !membre) return null;

  // Filtrer les chantiers (exclure le chantier actuel)
  const chantiersDisponibles = chantiers?.filter(
    (c) => c.id !== membre.chantier_id && ['en_cours', 'brouillon', 'projection'].includes(c.etat_chantier)
  );

  // Calcul des coûts : taux_horaire * coefficient_charges * heures
  const taux = membre.taux_horaire || 0;
  const coef = membre.coefficient_charges || 1.85;

  // Coût sur l'ancien chantier (heures du jour de rotation)
  const coutAncienChantier = heuresAncienChantier * taux * coef;
  // Coût sur le nouveau chantier (jour 1)
  const coutNouveauChantierJour1 = heuresJour1 * taux * coef;
  // Coût total du jour de rotation
  const coutJournalierTotal = coutAncienChantier + coutNouveauChantierJour1;

  // Couleurs spécialité
  const specialite = membre.specialite || membre.equipe_specialite;
  const colors = getSpecialiteColor(specialite);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation Zod
    const validation = rotationFormSchema.safeParse({
      nouveauChantierId,
      dateRotation,
      dateFinAffectation,
      heuresAncienChantier,
      heuresJour1,
      heuresJoursSuivants,
      notes,
    });
    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setLoading(true);
    try {
      // 1. Terminer l'ancienne affectation (date_fin = aujourd'hui avec heures_dernier_jour)
      if (membre.affectation_id) {
        const { error: errorTerminer } = await supabase
          .from('affectations_chantiers')
          .update({
            date_fin: dateRotation, // Termine aujourd'hui
            heures_dernier_jour: heuresAncienChantier > 0 ? heuresAncienChantier : null, // Heures travaillées aujourd'hui sur l'ancien chantier
            updated_at: new Date().toISOString(),
          })
          .eq('id', membre.affectation_id);

        if (errorTerminer) throw errorTerminer;
      }

      // 2. Créer la nouvelle affectation
      const { error: errorCreer } = await supabase.from('affectations_chantiers').insert({
        chantier_id: nouveauChantierId,
        membre_equipe_id: membre.id,
        equipe_id: membre.equipe_id || null,
        date_debut: dateRotation,
        date_fin: dateFinAffectation,
        heures_par_jour: heuresParJourMoyenne, // Moyenne pondérée heures jour 1 + jours suivants
        jours_travailles: joursOuvres || 1,
        taux_horaire_specifique: membre.taux_horaire,
        notes: notes || `Rotation depuis ${membre.chantier_actuel || 'chantier précédent'}`,
      });

      if (errorCreer) throw errorCreer;

      toast({
        title: 'Rotation effectuée',
        description: `${membre.prenom} ${membre.nom} : ${heuresAncienChantier}h sur ancien chantier, ${heuresJour1}h sur nouveau chantier`,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Erreur rotation:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'effectuer la rotation',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Nom du nouveau chantier sélectionné
  const nouveauChantierNom = chantiersDisponibles?.find((c) => c.id === nouveauChantierId)?.nom_chantier;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal - hauteur max avec scroll */}
      <div data-variant="industrial" className="relative w-full max-w-lg mx-4 bg-[#111111] border border-[#2A2A2A] rounded-[6px] shadow-[0_32px_80px_rgba(0,0,0,0.9),_inset_0_1px_0_rgba(255,255,255,0.04)] text-[#F2F2F2] overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A] flex-shrink-0" style={{background: "linear-gradient(90deg, #1C1C1C, #181818)"}}>
          <div className="flex items-center gap-3">
            <div className="w-[3px] h-[22px] rounded-[2px] flex-shrink-0" style={{background: "linear-gradient(180deg, #F7B500, #FF9500)"}} />
            <div>
              <h3 className="text-lg font-bold text-[#F2F2F2]">Rotation de membre</h3>
              <p className="text-xs text-white/25">Réaffecter sur un autre chantier</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-[#1C1C1C] border border-[#333] rounded-[6px] w-[30px] h-[30px] flex items-center justify-center text-white/35 hover:border-[#444] hover:text-white/60 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenu - scroll si nécessaire */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Info membre */}
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
            <div
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm',
                colors.dot
              )}
            >
              {membre.prenom?.[0]}{membre.nom?.[0]}
            </div>
            <div>
              <p className="font-semibold text-sm">{membre.prenom} {membre.nom}</p>
              <p className="text-xs text-muted-foreground">{membre.poste}</p>
            </div>
          </div>

          {/* Visualisation rotation */}
          <div className="flex items-center gap-2">
            <div className="flex-1 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
              <p className="text-[10px] text-red-400">Quitte</p>
              <p className="font-medium text-xs truncate">
                {membre.chantier_actuel || 'Aucun chantier'}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 p-2 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
              <p className="text-[10px] text-green-400">Rejoint</p>
              <p className="font-medium text-xs truncate">
                {nouveauChantierNom || '...'}
              </p>
            </div>
          </div>

          {/* Sélection nouveau chantier */}
          <div className="space-y-2">
            <Label htmlFor="chantier">Nouveau chantier</Label>
            <Select value={nouveauChantierId} onValueChange={setNouveauChantierId}>
              <SelectTrigger id="chantier">
                <SelectValue placeholder="Sélectionner un chantier..." />
              </SelectTrigger>
              <SelectContent>
                {chantiersDisponibles?.map((chantier) => (
                  <SelectItem key={chantier.id} value={chantier.id}>
                    {chantier.reference_chantier || chantier.nom_chantier} - {chantier.client}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dates de rotation */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="date">Date de début</Label>
              <Input
                id="date"
                type="date"
                value={dateRotation}
                onChange={(e) => setDateRotation(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateFin">Date de fin</Label>
              <Input
                id="dateFin"
                type="date"
                value={dateFinAffectation}
                min={dateRotation}
                onChange={(e) => handleDateFinChange(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Info jours ouvrés */}
          {joursOuvres > 0 && (
            <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">
                <strong>{joursOuvres}</strong> jour{joursOuvres > 1 ? 's' : ''} travaillé{joursOuvres > 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Heures sur l'ancien chantier aujourd'hui */}
          {membre.chantier_actuel && (
            <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5">
              <Label htmlFor="heuresAncienChantier" className="flex items-center gap-2 text-red-500 text-sm font-semibold">
                <Clock className="w-3.5 h-3.5" />
                Heures sur l'ancien chantier aujourd'hui
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Temps travaillé sur {membre.chantier_actuel} avant la rotation
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="heuresAncienChantier"
                  type="number"
                  value={heuresAncienChantier}
                  onChange={(e) => setHeuresAncienChantier(parseFloat(e.target.value) || 0)}
                  min={0}
                  max={12}
                  step={0.5}
                  className="w-20 h-9"
                />
                <span className="text-sm text-muted-foreground">heures</span>
              </div>
            </div>
          )}

          {/* Heures par jour - avec distinction jour 1 / jours suivants si plusieurs jours */}
          {joursOuvres === 1 ? (
            // Un seul jour
            <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/5">
              <Label htmlFor="heuresJour1" className="flex items-center gap-2 text-green-500 text-sm font-semibold">
                <Clock className="w-3.5 h-3.5" />
                Aujourd'hui sur {nouveauChantierNom || 'nouveau chantier'}
              </Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  id="heuresJour1"
                  type="number"
                  value={heuresJour1}
                  onChange={(e) => setHeuresJour1(parseFloat(e.target.value) || 4)}
                  min={0.5}
                  max={12}
                  step={0.5}
                  className="w-20 h-9"
                  required
                />
                <span className="text-sm text-muted-foreground">heures</span>
              </div>
            </div>
          ) : joursOuvres > 1 ? (
            // Plusieurs jours : distinction jour 1 / jours suivants
            <div className="space-y-3">
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                <Label htmlFor="heuresJour1" className="flex items-center gap-2 text-amber-500 text-sm font-semibold">
                  <Clock className="w-3.5 h-3.5" />
                  Aujourd'hui (jour de rotation) sur {nouveauChantierNom || 'nouveau chantier'}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="heuresJour1"
                    type="number"
                    value={heuresJour1}
                    onChange={(e) => setHeuresJour1(parseFloat(e.target.value) || 4)}
                    min={0.5}
                    max={12}
                    step={0.5}
                    className="w-20 h-9"
                  />
                  <span className="text-sm text-muted-foreground">heures</span>
                </div>
              </div>

              <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
                <Label htmlFor="heuresSuivants" className="flex items-center gap-2 text-primary text-sm font-semibold">
                  <Clock className="w-3.5 h-3.5" />
                  Jours suivants ({joursOuvres - 1} jour{joursOuvres > 2 ? 's' : ''}) sur {nouveauChantierNom || 'nouveau chantier'}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="heuresSuivants"
                    type="number"
                    value={heuresJoursSuivants}
                    onChange={(e) => setHeuresJoursSuivants(parseFloat(e.target.value) || 8)}
                    min={0.5}
                    max={12}
                    step={0.5}
                    className="w-20 h-9"
                  />
                  <span className="text-sm text-muted-foreground">heures/jour</span>
                </div>
              </div>
            </div>
          ) : null}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Raison de la rotation, instructions particulières..."
              rows={2}
            />
          </div>

          {/* Résumé coût du jour de rotation */}
          <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Euro className="w-4 h-4" />
              <span>Coût du jour de rotation</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Ancien chantier ({heuresAncienChantier}h)</span>
              <span>{coutAncienChantier.toFixed(0)}€</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Nouveau chantier ({heuresJour1}h)</span>
              <span>{coutNouveauChantierJour1.toFixed(0)}€</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-primary/20">
              <span className="text-sm font-medium">Total ({heuresAncienChantier + heuresJour1}h)</span>
              <span className="text-base font-bold text-foreground">{coutJournalierTotal.toFixed(0)}€</span>
            </div>
          </div>

          {/* Boutons - toujours visibles */}
          <div className="flex gap-3 pt-2 sticky bottom-0 bg-[#111111] border-t border-[#1E1E1E] -mx-4 px-4 py-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="flex-1 text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent"
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]"
              disabled={!nouveauChantierId || !dateFinAffectation || joursOuvres <= 0 || loading}
            >
              {loading ? 'Rotation...' : 'Confirmer la rotation'}
            </Button>
          </div>
        </form>
      </div>

      {/* Dialogue de confirmation weekend */}
      <WeekendConfirmDialog
        isOpen={showWeekendDialog}
        onClose={() => {
          setShowWeekendDialog(false);
          setPendingDateFin('');
        }}
        onConfirm={handleWeekendConfirm}
        dateFin={pendingDateFin ? parseISO(pendingDateFin) : new Date()}
        type="membre"
      />
    </div>
  );
};

export default ModalRotation;
